import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  formatJakartaEndOfDay,
  formatJakartaStartOfDay,
} from "../../utils/date";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const SUCCESS_CODES = new Set([200, 201, 204]);

const parseEnvelope = async (response) => {
  const isJson = response.headers
    .get("content-type")
    ?.toLowerCase()
    .includes("application/json");
  const payload = isJson ? await response.json() : null;

  if (isUnauthorizedApiResponse(response, payload)) {
    forceLogoutToLogin();
  }

  if (!payload || !SUCCESS_CODES.has(payload.resultCode)) {
    let message =
      payload?.resultDesc ||
      (response.ok ? "Unexpected response from server." : response.statusText);

    if (payload?.errors) {
      const errorMessages = Object.values(payload.errors)
        .flat()
        .filter(Boolean);
      if (errorMessages.length) {
        message = errorMessages.join(", ");
      }
    }

    const error = new Error(message);
    error.payload = payload;
    throw error;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  return payload;
};

const getAuthHeaders = (state) => {
  const token = state.auth?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildTicketsQuery = (state) => {
  const { filters, pagination, sort } = state.tickets;
  const params = new URLSearchParams();
  params.append("page", pagination.page + 1);
  params.append("pageSize", pagination.pageSize);
  if (filters.search.trim()) {
    params.append("search", filters.search.trim());
  }
  if (filters.eventId) {
    params.append("eventId", filters.eventId);
  }
  if (filters.startDate) {
    params.append("startDate", formatJakartaStartOfDay(filters.startDate));
  }
  if (filters.endDate) {
    params.append("endDate", formatJakartaEndOfDay(filters.endDate));
  }
  if (sort?.field) {
    params.append("sortBy", sort.field);
    params.append("sortDirection", sort.direction || "asc");
  }
  return params.toString();
};

export const fetchTickets = createAsyncThunk(
  "tickets/fetchList",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const query = buildTicketsQuery(state);
      const response = await fetch(`${API_BASE_URL}/tickets?${query}`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(state),
        },
      });
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load tickets.");
    }
  }
);

export const fetchTicketEventOptions = createAsyncThunk(
  "tickets/fetchEvents",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/events?all=true&sortBy=created_datetime&sortDirection=desc`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(state),
        },
      });
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load events.");
    }
  }
);

const initialMeta = {
  page: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
};

const initialState = {
  items: [],
  meta: { ...initialMeta },
  pagination: {
    page: 0,
    pageSize: 10,
  },
  filters: {
    search: "",
    eventId: "",
    startDate: null,
    endDate: null,
  },
  sort: {
    field: "created_datetime",
    direction: "desc",
  },
  loading: false,
  error: null,
  events: [],
  eventsStatus: "idle",
  eventsError: null,
};

const ticketsSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    setTicketSearchFilter: (state, action) => {
      state.filters.search = action.payload || "";
      state.pagination.page = 0;
    },
    setTicketEventFilter: (state, action) => {
      state.filters.eventId = action.payload || "";
      state.pagination.page = 0;
    },
    setTicketDateRange: (state, action) => {
      state.filters.startDate = action.payload.startDate || null;
      state.filters.endDate = action.payload.endDate || null;
      state.pagination.page = 0;
    },
    setTicketPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setTicketPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 0;
    },
    setTicketSort: (state, action) => {
      const { field, direction } = action.payload || {};
      if (!field) return;
      state.sort = {
        field,
        direction: direction === "desc" ? "desc" : "asc",
      };
      state.pagination.page = 0;
    },
    resetTicketFilters: (state) => {
      state.filters = { ...initialState.filters };
      state.pagination.page = 0;
      state.sort = { ...initialState.sort };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        const items =
          payload.items ||
          payload.data?.items ||
          (Array.isArray(payload) ? payload : []);
        const meta = payload.meta || payload.data?.meta || {};
        state.items = Array.isArray(items) ? items : [];
        state.meta = {
          ...initialMeta,
          ...meta,
        };
        if (typeof state.meta.page === "number") {
          state.pagination.page = Math.max(state.meta.page - 1, 0);
        }
        if (typeof state.meta.pageSize === "number") {
          state.pagination.pageSize = state.meta.pageSize;
        }
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load tickets.";
      })
      .addCase(fetchTicketEventOptions.pending, (state) => {
        state.eventsStatus = "loading";
        state.eventsError = null;
      })
      .addCase(fetchTicketEventOptions.fulfilled, (state, action) => {
        state.eventsStatus = "succeeded";
        const payload = action.payload;
        const list =
          payload?.items ||
          payload?.data?.items ||
          payload?.data ||
          payload ||
          [];
        state.events = Array.isArray(list) ? list : [];
      })
      .addCase(fetchTicketEventOptions.rejected, (state, action) => {
        state.eventsStatus = "failed";
        state.eventsError = action.payload || "Failed to load events.";
      });
  },
});

export const {
  setTicketSearchFilter,
  setTicketEventFilter,
  setTicketDateRange,
  setTicketPage,
  setTicketPageSize,
  setTicketSort,
  resetTicketFilters,
} = ticketsSlice.actions;

export const selectTicketItems = (state) => state.tickets.items;
export const selectTicketMeta = (state) => state.tickets.meta;
export const selectTicketPagination = (state) => state.tickets.pagination;
export const selectTicketFilters = (state) => state.tickets.filters;
export const selectTicketSort = (state) => state.tickets.sort;
export const selectTicketLoading = (state) => state.tickets.loading;
export const selectTicketError = (state) => state.tickets.error;
export const selectTicketEvents = (state) => state.tickets.events;
export const selectTicketEventsStatus = (state) => state.tickets.eventsStatus;

export default ticketsSlice.reducer;
